import { Box, IconButton, Avatar, Menu, MenuItem, Typography, Divider, Tooltip } from '@mui/material';
import { FC, useState } from 'react';
import InboxTabs from './InboxTabs';
import { isAdmin } from '@/services/featureFlag';
import SupportIcon from '@mui/icons-material/Support';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import useUser from '@/hooks/useUser';
import { observer } from 'mobx-react';
import { ROLE_ADMIN, ROLE_SECRETARY, ROLE_DOCTOR } from '@/config/constants';
import { isInsideElectron } from '@/utils/misc';
import { useNavigate } from 'react-router-dom';
import { PATH_NEW_CASE } from '@/config/paths';
import WebNewCaseDialog from '../dialogs/web-only/WebNewCaseDialog';

const getRoleName = (role: string): string => {
  switch (role) {
    case ROLE_ADMIN:
      return 'Administrator';
    case ROLE_SECRETARY:
      return 'Sekretær';
    case ROLE_DOCTOR:
      return 'Læge';
    default:
      return role;
  }
};

interface InboxTopBarContentProps {
  onRefreshList?: () => void;
  isRefreshLoading?: boolean;
}

const InboxTopBarContent: FC<InboxTopBarContentProps> = observer(
({ onRefreshList, isRefreshLoading = false }) => {
  const user = useUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newCaseDialogOpen, setNewCaseDialogOpen] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRestartApp = () => {
    window.location.reload();
  };

  const handleOpenNewCase = () => {
    setNewCaseDialogOpen(true);
    handleClose();
  };

  const handleCloseNewCase = () => {
    setNewCaseDialogOpen(false);
  };

  const handleNewCaseSelection = () => {
    navigate(PATH_NEW_CASE);
    handleCloseNewCase();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 6,
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexGrow: 1,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <InboxTabs />
        <Tooltip title="Opdater sagliste" arrow>
          <span>
            <IconButton
              onClick={onRefreshList}
              size="small"
              sx={{ ml: 1 }}
              disabled={!onRefreshList || isRefreshLoading}
              aria-label="Opdater sagliste"
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: 1 }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.main',
              fontSize: '14px',
            }}
          >
            {user.initials}
          </Avatar>
        </IconButton>
        <Menu
          id="user-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'user-avatar-button',
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user.name}
            </Typography>
            {user.email && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {user.email}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {getRoleName(user.getPrimaryRole())}
              </Typography>
              <Tooltip title="Kontakt IT for at anmode om rolleændring" arrow>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Box>
          <Divider />
          {!isInsideElectron() && (
            <MenuItem
              onClick={handleOpenNewCase}
              sx={{ gap: 1.5 }}
            >
              <AddIcon fontSize="small" />
              <Typography variant="body2">Ny sag</Typography>
            </MenuItem>
          )}
          {isAdmin() && (
            <MenuItem
              onClick={handleClose}
              id="intercom_opener"
              sx={{ gap: 1.5 }}
            >
              <SupportIcon fontSize="small" />
              <Typography variant="body2">Support</Typography>
            </MenuItem>
          )}
          <MenuItem
            onClick={handleRestartApp}
            sx={{ gap: 1.5 }}
          >
            <RestartAltIcon fontSize="small" />
            <Typography variant="body2">Genstart app</Typography>
          </MenuItem>
        </Menu>
      </Box>
      {!isInsideElectron() && (
        <WebNewCaseDialog
          open={newCaseDialogOpen}
          onClose={handleCloseNewCase}
          onDone={handleNewCaseSelection}
        />
      )}
    </Box>
  );
});
export default InboxTopBarContent;
